import React from 'react';

// Add custom CSS for gentle hover animation
const styles = `
  @keyframes gentle-hover {
    0%, 100% {
      margin-top: -75px;
    }
    50% {
      margin-top: -83px;
    }
  }
`;

// Inject styles into the document head
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.type = 'text/css';
  styleSheet.innerText = styles;
  document.head.appendChild(styleSheet);
}

export interface PotionAttempt {
  attemptNumber: number;
  ingredients: string[];
  result: {
    shape: string;
    color: string;
    nameStyle: string;
    potionName: string;
    isLovePotion: boolean;
    videoPath?: string;
  };
}

interface PotionAttemptDisplayProps {
  attempt: PotionAttempt;
  className?: string;
  size?: 'small' | 'medium' | 'large';
  displayMode?: 'default' | 'win' | 'lose';
}

export const PotionAttemptDisplay: React.FC<PotionAttemptDisplayProps> = ({ 
  attempt, 
  className = '', 
  size = 'medium',
  displayMode = 'default'
}) => {
  const { attemptNumber, result } = attempt;
  const { shape, color, potionName, isLovePotion } = result;

  // Size configurations - massive sizes
  const sizeClasses = {
    small: 'w-200 h-250',
    medium: 'w-240 h-300', 
    large: 'w-300 h-375'
  };

  const textSizeClasses = {
    small: 'text-base',
    medium: 'text-lg',
    large: 'text-xl'
  };

  // Map potion result to image path
  const getPotionImagePath = (shape: string, color: string) => {
    const shapeMap: Record<string, string> = {
      'Heart': 'heart',
      'Weird': 'weird', 
      'Normal': 'normal'
    };
    
    const colorMap: Record<string, string> = {
      'Pink': 'pink',
      'Blue': 'blue',
      'Green': 'green', 
      'Yellow': 'yellow',
      'Black': 'black',
      'Rainbow': 'rainbow'
    };
    
    const shapeName = shapeMap[shape] || 'normal';
    const colorName = colorMap[color] || 'green';
    
    return `/img/potions/${shapeName}-${colorName}.png`;
  };

  const potionImagePath = getPotionImagePath(shape, color);

  // Special animation for the actual winning Love Potion (not just Heart+Pink shape/color)
  const isWinningLovePotion = isLovePotion;

  // Different layouts based on display mode
  if (displayMode === 'lose') {
    return (
      <div className={`flex flex-col items-center space-y-2 ${className} relative`}>
        {/* Large Lose Layout - matching win celebration size */}
        <div className="relative flex items-center justify-center" style={{ width: '320px', height: '200px' }}>
          <img 
            src={potionImagePath}
            alt={`${shape} ${color} Potion`}
            style={{ width: '320px', height: '400px', marginTop: '-100px' }}
            className="object-contain"
            onError={(e) => {
              console.error('Failed to load potion image:', potionImagePath);
              e.currentTarget.style.display = 'none';
            }}
          />
        </div>
        
        <div className="font-bold text-white text-2xl pt-12">
          Attempt {attemptNumber}
        </div>
        
        <div className="text-center text-white/90 font-medium text-xl max-w-80">
          {potionName}
        </div>
        
        <div className="text-center text-white/70 text-base max-w-80">
          {attempt.ingredients.join(', ')}
        </div>
      </div>
    );
  }

  if (displayMode === 'win') {
    return (
      <div className={`flex flex-col items-center space-y-2 ${className} relative`}>
        {/* Celebratory Win Layout */}
        <div className="relative flex items-center justify-center" style={{ width: '320px', height: '200px' }}>
          <img 
            src={potionImagePath}
            alt={`${shape} ${color} Potion`}
            style={{ 
              width: '320px', 
              height: '400px',
              marginTop: '-100px',
              ...(isWinningLovePotion ? {
                animation: 'gentle-hover 2s ease-in-out infinite'
              } : {})
            }}
            className="object-contain"
            onError={(e) => {
              console.error('Failed to load potion image:', potionImagePath);
              e.currentTarget.style.display = 'none';
            }}
          />
          
          {isLovePotion && (
            <div className="absolute -top-4 -right-4 text-yellow-300 z-20 text-2xl">
              ✨
            </div>
          )}
        </div>
        
        <div className="font-bold text-white text-2xl pt-12">
          Attempt {attemptNumber}
        </div>
        
        <div className="text-center text-white/90 font-medium text-xl max-w-80">
          {potionName}
        </div>
        
        <div className="text-center text-white/70 text-base max-w-80">
          {attempt.ingredients.join(', ')}
        </div>
      </div>
    );
  }

  // Default layout (existing behavior)
  return (
    <div className={`flex flex-col items-center space-y-2 ${className} relative`}>
      {/* Bottle Display - Create space for the image */}
      <div className="relative flex items-center justify-center w-full h-32">
        {/* Potion Image - positioned to extend above but still be visible */}
        <img 
          src={potionImagePath}
          alt={`${shape} ${color} Potion`}
          className={`${sizeClasses[size]} object-contain absolute top-0 left-1/2 z-10 ${isWinningLovePotion ? '' : 'transform -translate-x-1/2 -translate-y-1/2'}`}
          style={isWinningLovePotion ? {
            animation: 'gentle-hover 2s ease-in-out infinite'
          } : {}}
          onError={(e) => {
            console.error('Failed to load potion image:', potionImagePath);
            // Fallback to a default image or hide
            e.currentTarget.style.display = 'none';
          }}
        />
        
        {/* Success Indicator */}
        {isLovePotion && (
          <div className="absolute -top-4 -right-4 text-yellow-300 z-20">
            ✨
          </div>
        )}
      </div>
      
      {/* Attempt Number - positioned below the image space */}
      <div className="font-bold text-white text-xl z-30 relative mt-6 mb-6">
        Attempt {attemptNumber}
      </div>
      
      {/* Potion Name */}
      <div className={`text-center text-white/90 font-medium ${textSizeClasses[size]} max-w-80`}>
        <div title={potionName}>
          {potionName}
        </div>
      </div>
      
      {/* Ingredients List */}
      <div className={`text-center text-white/70 ${size === 'large' ? 'text-base' : 'text-sm'} max-w-80`}>
        {attempt.ingredients.join(', ')}
      </div>
    </div>
  );
};